import { inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { delay, retry, take, tap } from 'rxjs/operators';
import { ApiService, UtilsService } from 'shared-lib';
import { HttpClient } from '@angular/common/http';
import { environment } from 'projects/self-care/src/environments/environment';
import { NotificationService } from './notification.service';
import { LocalStorageService } from '../../core/storage/local-storage.service';
import { LOCAL_STORAGE_KEYS } from '../../core/storage/storage.constant';
import { USER_STORAGE_KEY } from '../../core/user/user.type';
import { SharedDataService } from './shared-data.service';

const URL = 'v1/superapp/web-push';
const BASE_URL = environment.baseUrl + '/api/' + URL;

export type WebPushSubscribeResponse = { id: number };

export type WebPushUnsubscribeRequest = {
  id?: number;
  logoutAll?: boolean;
  linkedAccountUsernames?: string[];
};

@Injectable({
  providedIn: 'root',
})
export class PushNotificationService {
  private readonly utils = inject(UtilsService);
  private readonly httpClient = inject(HttpClient);
  private readonly api = inject(ApiService);
  private readonly notificationService = inject(NotificationService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly sharedDataService = inject(SharedDataService);

  private subscribeInFlight: Promise<void> | null = null;

  constructor() {
    this.listenToServiceWorkerMessages();
  }

  private listenToServiceWorkerMessages(): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        const notification = event.data?.payload;
        if (event.data?.type === 'PUSH_NOTIFICATION' && this.isUserLoggedIn() && notification) {
          this.notificationService.getUnreadCount();
          this.notificationService.getAllNotification({}, true);
          this.utils.showPushNotification(
            notification?.body?.title ?? notification?.title,
            notification?.body?.content ?? notification?.body
          );
        }
      });
    }
  }

  async getPublicKey(): Promise<string> {
    const res = await firstValueFrom(this.httpClient.get(`${BASE_URL}/public-key`, { responseType: 'text' }));
    return res as string;
  }

  private isUserLoggedIn(): boolean {
    return !!this.localStorageService.getItem(USER_STORAGE_KEY);
  }

  getSubscriptionId(): number | null {
    const value = this.localStorageService.getItemObject<number>(LOCAL_STORAGE_KEYS.WEB_PUSH_SUBSCRIPTION_ID);
    return typeof value === 'number' ? value : null;
  }

  getReferenceId(): number | null {
    const value = this.localStorageService.getItemObject<number>(LOCAL_STORAGE_KEYS.WEB_PUSH_REFERENCE_ID);
    return typeof value === 'number' ? value : null;
  }

  private persistSubscribeResponse(res: WebPushSubscribeResponse): void {
    this.localStorageService.setItemObject(LOCAL_STORAGE_KEYS.WEB_PUSH_SUBSCRIPTION_ID, res.id);
    if (this.getReferenceId() === null) {
      this.localStorageService.setItemObject(LOCAL_STORAGE_KEYS.WEB_PUSH_REFERENCE_ID, res.id);
    }
  }

  private clearStoredSubscriptionIds(): void {
    this.localStorageService.removeItem(LOCAL_STORAGE_KEYS.WEB_PUSH_SUBSCRIPTION_ID);
    this.localStorageService.removeItem(LOCAL_STORAGE_KEYS.WEB_PUSH_REFERENCE_ID);
  }

  checkNotificationStatus(): 'granted' | 'denied' | 'default' | 'unsupported' {
    if (!('Notification' in window && 'serviceWorker' in navigator)) {
      return 'unsupported';
    }
    return Notification.permission as 'granted' | 'denied' | 'default';
  }

  /** False only when user explicitly opted out (e.g. profile switch). Unset defaults to allowed. */
  isNotificationAllowedByUser(): boolean {
    const value = this.localStorageService.getItemObject<boolean>(LOCAL_STORAGE_KEYS.NOTIFICATIONS_ALLOWED);
    return value !== false;
  }

  /**
   * After login / page load: ensure browser + backend subscription exist and store backend id.
   * After switch-account: re-POST with referenceId so backend rebinds the row.
   */
  initializePushNotifications(options?: { accountSwitch?: boolean }): void {
    if (!this.isNotificationAllowedByUser()) {
      return;
    }
    this.requestNotificationPermission(options);
  }

  requestNotificationPermission(options?: { accountSwitch?: boolean }): void {
    if (!this.isUserLoggedIn()) {
      return;
    }
    void this.handleSubscriptionFlow(options);
  }

  private async handleSubscriptionFlow(options?: { accountSwitch?: boolean }): Promise<void> {
    const storedId = this.getSubscriptionId();
    const active = await this.isSubscriptionActive();

    // Already registered with backend for this session — skip unless account switch needs rebind.
    if (active && storedId !== null && !options?.accountSwitch) {
      return;
    }

    this.syncWithBrowserPermission(options);
  }

  private syncWithBrowserPermission(options?: { accountSwitch?: boolean }): void {
    const status = this.checkNotificationStatus();
    if (status === 'unsupported' || status === 'denied') {
      return;
    }
    if (status === 'granted') {
      void this.subscribeToPush(options);
      return;
    }
    void Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          void this.subscribeToPush(options);
        }
      })
      .catch((error) => {
        console.error('Error requesting notification permission:', error);
      });
  }

  async subscribeToPush(options?: { accountSwitch?: boolean }): Promise<void> {
    if (this.subscribeInFlight) {
      return this.subscribeInFlight;
    }

    this.subscribeInFlight = this.performSubscribe(options).finally(() => {
      this.subscribeInFlight = null;
    });
    return this.subscribeInFlight;
  }

  private async performSubscribe(options?: { accountSwitch?: boolean }): Promise<void> {
    try {
      const registration = await navigator.serviceWorker.ready;
      if (!registration.pushManager) {
        this.utils.showError('Push notifications are not supported in this context.');
        return;
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        const publicKey = await this.getPublicKey();
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(publicKey) as BufferSource,
        });
      }

      await firstValueFrom(this.sendSubscriptionToServer(subscription, options));
    } catch (err) {
      console.error('Failed to subscribe:', err);
      this.utils.showError('Failed to subscribe to notifications.');
    }
  }

  private sendSubscriptionToServer(
    subscription: PushSubscription,
    options?: { accountSwitch?: boolean }
  ): Observable<WebPushSubscribeResponse> {
    const referenceId = this.getReferenceId();
    const data: Record<string, unknown> = {
      ...subscription.toJSON(),
    };

    if (options?.accountSwitch && referenceId !== null) {
      data['referenceId'] = referenceId;
    }

    return this.api.post<WebPushSubscribeResponse>(URL + '/subscribe', data, null, '1.0').pipe(
      retry({
        count: 3,
        delay: (error) => of(error).pipe(delay(15000)),
      }),
      tap((res) => {
        if (res?.id !== undefined && res?.id !== null) {
          this.persistSubscribeResponse(res);
        }
      }),
      take(1)
    );
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
  }

  async isSubscriptionActive(): Promise<boolean> {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return !!subscription;
    } catch {
      return false;
    }
  }

  private getLinkedAccountUsernames(): string[] {
    const accounts = this.sharedDataService.linkedAccounts() ?? [];
    return accounts
      .map(
        (acc: { customerLinkedAccountCode?: string; customerCode?: string; username?: string; id?: string | number }) =>
          acc.customerLinkedAccountCode ||
          acc.customerCode ||
          acc.username ||
          (acc.id !== undefined && acc.id !== null ? String(acc.id) : null)
      )
      .filter((name): name is string => !!name);
  }

  /**
   * Unsubscribes on the backend by stored id (or logoutAll payload), then clears the browser subscription.
   * Always clears local ids — even when the server call fails (e.g. expired session on in-app logout).
   */
  async unsubscribeFromPush(options?: { logoutAll?: boolean; skipServer?: boolean }): Promise<void> {
    const subscriptionId = this.getSubscriptionId();

    try {
      if (!options?.skipServer) {
        const body: WebPushUnsubscribeRequest = options?.logoutAll
          ? {
              logoutAll: true,
              linkedAccountUsernames: this.getLinkedAccountUsernames(),
            }
          : {
              id: subscriptionId ?? undefined,
              logoutAll: false,
            };

        // Single logout requires an id; skip the API call if we never got one.
        if (options?.logoutAll || (body.id !== undefined && body.id !== null)) {
          await firstValueFrom(this.api.post(URL + '/unsubscribe', body, null, '1.0').pipe(take(1)));
        }
      }
    } catch (err) {
      console.error('Server unsubscribe failed:', err);
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (err) {
      console.error('Browser unsubscribe failed:', err);
    } finally {
      this.clearStoredSubscriptionIds();
    }
  }
}
